import Backdrop from '@material-ui/core/Backdrop'
import Popper, { PopperPlacementType } from '@material-ui/core/Popper'
import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import { ReferenceObject } from 'popper.js'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => {
    const darkThemeArrow = `10px solid ${theme.palette.grey[700]}`
    const lightThemeArrow = '0px solid transparent'
    const borderColor = '#c9c9c9'
    return {
        popoutBody: {
            zIndex: theme.zIndex.drawer + 2,
            margin: theme.spacing(1.25),
            borderRadius: 8,
            padding: theme.spacing(2),
            color: theme.palette.common.white,
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
            '&.dark': {
                backgroundColor: theme.palette.grey[700],
            },
            '&.light': {
                backgroundColor: theme.palette.common.white,
            },
            '&[x-placement*="top"]': {
                '& .arrow': {
                    height: 0,
                    width: 0,
                    borderTop: `10px solid ${borderColor}`,
                    borderBottom: '1px solid transparent',
                    borderLeft: '10px solid transparent',
                    borderRight: '10px solid transparent',
                    position: 'absolute',
                    bottom: -theme.spacing(1.25),
                },
                '& .arrow.dark': {
                    borderTop: darkThemeArrow,
                },
                '& .arrow.light': {
                    borderBottom: lightThemeArrow,
                },
            },
            '&[x-placement*="bottom"]': {
                '& .arrow': {
                    height: 0,
                    width: 0,
                    borderTop: '1px solid transparent',
                    borderBottom: `10px solid ${borderColor}`,
                    borderLeft: '10px solid transparent',
                    borderRight: '10px solid transparent',
                    position: 'absolute',
                    top: -theme.spacing(1.25),
                },
                '& .arrow.dark': {
                    borderBottom: darkThemeArrow,
                },
                '& .arrow.light': {
                    borderTop: lightThemeArrow,
                },
            },
            '&[x-placement*="right"]': {
                '& .arrow': {
                    height: 0,
                    width: 0,
                    borderTop: '10px solid transparent',
                    borderBottom: '10px solid transparent',
                    borderLeft: '1px solid transparent',
                    borderRight: `10px solid ${borderColor}`,
                    position: 'absolute',
                    left: -theme.spacing(1.25),
                },
                '& .arrow.dark': {
                    borderRight: darkThemeArrow,
                },
                '& .arrow.light': {
                    borderLeft: lightThemeArrow,
                },
            },
            '&[x-placement*="left"]': {
                '& .arrow': {
                    height: 0,
                    width: 0,
                    borderTop: '10px solid transparent',
                    borderBottom: '10px solid transparent',
                    borderLeft: `10px solid  ${borderColor}`,
                    borderRight: '1px solid transparent',
                    position: 'absolute',
                    right: -theme.spacing(1.25),
                },
                '& .arrow.dark': {
                    borderLeft: darkThemeArrow,
                },
                '& .arrow.light': {
                    borderRight: lightThemeArrow,
                },
            },
        },
        backdrop: { zIndex: theme.zIndex.drawer + 1 },
    }
})

export interface PopoutStyleProps {}

export interface PopoutProps extends Partial<PopoutStyleProps> {
    /**
     * Anchor element
     */
    anchorEl?: null | ReferenceObject | (() => ReferenceObject) | HTMLElement

    placement?: PopperPlacementType

    offset?: string

    onCloseRequest?: () => void

    className?: string

    theme?: string

    isForSearch?: boolean
}

const Popout: React.FC<PopoutProps> = ({
    anchorEl,
    onCloseRequest,
    placement = 'right',
    offset,
    children,
    className,
    theme = 'light',
}) => {
    const classes = useStyles()
    const backdropRef = React.createRef<any>()
    const popperRef = React.createRef<any>()
    const popperContentRef = React.createRef<any>()
    const [arrowRef, setArrowRef] = React.useState<null | HTMLDivElement>(null)

    const getFocusableElements = React.useCallback(() => {
        if (popperContentRef.current) {
            return Array.from(
                popperContentRef.current.querySelectorAll(
                    'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
                ) as NodeListOf<HTMLElement>
            ).filter(
                (el) =>
                    !el.hasAttribute('disabled') &&
                    !el.getAttribute('aria-hidden')
            )
        } else {
            return []
        }
    }, [popperContentRef])

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key !== 'Tab') return

            const focusableElements = getFocusableElements()
            const firstElement = focusableElements[0]
            const lastElement = focusableElements[focusableElements.length - 1]

            if (!focusableElements.length) return

            if (event.shiftKey && document.activeElement === firstElement) {
                // focus on anchor element when shift + tabbed on the first element
                event.preventDefault()
                ;(anchorEl as HTMLElement).focus()
                onCloseRequest?.()
            } else if (
                !event.shiftKey &&
                document.activeElement === lastElement
            ) {
                // focus on anchor element when tabbed on the last element
                event.preventDefault()
                ;(anchorEl as HTMLElement).focus()
                onCloseRequest?.()
            }
        },
        [anchorEl, getFocusableElements, onCloseRequest]
    )

    const handleFocusOut = React.useCallback(
        (event: FocusEvent) => {
            if (
                popperContentRef.current &&
                !popperContentRef.current.contains(event.relatedTarget as Node)
            ) {
                onCloseRequest?.()
            }
        },
        [
            onCloseRequest,
            popperContentRef,
            anchorEl,
            getFocusableElements,
            popperContentRef,
        ]
    )

    React.useEffect(() => {
        // focus the first element in the popper when it is opened
        const timeout = setTimeout(() => {
            const firstElement = getFocusableElements()[0]
            if (anchorEl && firstElement) {
                firstElement.focus()
            }
        }, 0)

        // add focusout event listener to popper when tabbing out of the popper
        const popperNode = popperContentRef.current
        if (anchorEl) {
            popperNode?.addEventListener('focusout', handleFocusOut)
        }

        return () => {
            clearTimeout(timeout)
            popperNode?.removeEventListener('focusout', handleFocusOut)
        }
    }, [anchorEl, popperContentRef])

    const updateRef = (element: HTMLDivElement) => {
        setArrowRef(element)
    }
    return (
        <Backdrop
            ref={backdropRef}
            open={anchorEl != null}
            invisible={true}
            className={classes.backdrop}
            data-testid="popoutBackdrop"
            onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                if (event.target === backdropRef.current) {
                    onCloseRequest?.()
                }
            }}
            onKeyDown={(event) => {
                if (onCloseRequest && event.key === 'Escape') {
                    onCloseRequest?.()
                    // fous on anchor element when poppper closed
                    if (anchorEl instanceof HTMLElement) {
                        anchorEl.focus()
                    }
                }
            }}
        >
            <Popper
                popperRef={popperRef}
                className={clsx(classes.popoutBody, className, theme)}
                modifiers={{
                    flip: {
                        enabled: true,
                    },
                    arrow: {
                        enabled: true,
                        element: arrowRef,
                    },
                    offset: {
                        enabled: offset && true,
                        offset: offset && offset,
                    },
                    preventOverflow: {
                        enabled: true,
                        boundariesElement: 'viewport',
                        padding: 14,
                    },
                }}
                placement={placement}
                open={anchorEl != null}
                anchorEl={anchorEl}
                onKeyDown={handleKeyDown}
            >
                <div ref={popperContentRef}>
                    <div ref={updateRef} className={clsx('arrow', theme)}></div>
                    {children}
                </div>
            </Popper>
        </Backdrop>
    )
}

export default Popout
